import { createContext, useMemo } from 'react';
import { CartContextValue } from './CartProvider.types';
import useAuthContext from '../AuthProvider/useAuthContext';
import { useFeatureValue } from '@growthbook/growthbook-react';
import { FEATURE_FLAGS } from 'src/utils/growthBook/growthBook.constants.ts';
import useCartQuery from 'src/features/cart/presentation/hooks/useCartQuery';
import useCartActions from 'src/context/CartProvider/useCartActions';
import calculateCartSummary from 'src/utils/carts/calculateCartSummary';

const initialState = {
  cartproducts: [],
  sub_total: '0',
  delivery_price: 0,
  total: 0,
  errors: [],
  totalQuantity: 0,
  total_with_coupon: 0,
  isSomeProductOutOfStock: false,
  isSomeProductNotEnoughStock: false,
};

export const CartContext = createContext<CartContextValue>(initialState);

CartContext.displayName = 'CartContext';

export const CartProvider = ({ children }: React.PropsWithChildren) => {
  const { isB2b } = useAuthContext();
  const {
    addProductToCart,
    removeProductFromCart,
    createUserCart,
    isUpdatingCart,
    isMigratingProducts,
  } = useCartActions();
  const cartQuery = useCartQuery();
  const b2bDeliveryFee = useFeatureValue(FEATURE_FLAGS.B2B_DELIVERY_FEE, 68);
  const cartproducts = cartQuery.data?.products || [];
  const cartSummary = useMemo(
    () =>
      calculateCartSummary({
        cartData: cartQuery.data,
        b2bDeliveryFee,
        isB2b,
      }),
    [cartQuery.data, b2bDeliveryFee, isB2b]
  );

  const isSomeProductOutOfStock = cartproducts.some((product) => {
    const isOutOfStock = product.available_quantity === 0;
    return isOutOfStock;
  });

  const isSomeProductNotEnoughStock = cartproducts.some((product) => {
    const notEnoughStock = product.available_quantity < product.quantity;
    return notEnoughStock;
  });

  const errors = cartQuery.error ? [cartQuery.error.message] : [];

  return (
    <CartContext.Provider
      value={{
        ...cartSummary,
        cartproducts,
        errors,
        addProductToCart,
        removeProductFromCart,
        createUserCart,
        isCartLoading: cartQuery.isLoading || isMigratingProducts,
        isUpdatingCart,
        cartId: cartQuery.data?.pk,
        isCartFetching: cartQuery.isFetching,
        isSomeProductOutOfStock,
        isSomeProductNotEnoughStock,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
